import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, Link as LinkIcon } from "lucide-react";

import { ArchiveFilters } from "@/components/ArchiveFilters";
import { EntryDetailPanel } from "@/components/EntryDetailPanel";
import { SectionIntro } from "@/components/SectionIntro";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { fetchLibrary, fetchLibraryItem } from "@/lib/api";
import { pickText } from "@/lib/content";

export const ArchivePage = ({ language }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Handle deep-linking from URL
  useEffect(() => {
    const entrySlug = searchParams.get("entry");
    if (entrySlug && !selectedEntry) {
      const loadEntryFromUrl = async () => {
        try {
          const entry = await fetchLibraryItem(entrySlug);
          if (entry) {
            setSelectedEntry(entry);
          }
        } catch (error) {
          console.error("Failed to load entry from URL:", error);
        }
      };
      loadEntryFromUrl();
    }
  }, [searchParams]);

  // Update URL when entry is selected
  const handleSelectEntry = (entry) => {
    setSelectedEntry(entry);
    if (entry?.slug) {
      setSearchParams({ entry: entry.slug });
    }
  };

  // Copy shareable link
  const handleCopyLink = () => {
    if (selectedEntry?.slug) {
      const url = `${window.location.origin}/archive?entry=${selectedEntry.slug}`;
      navigator.clipboard.writeText(url).then(() => {
        toast.success(pickText(language, "Link copied to clipboard!", "Link copiato negli appunti!"));
      }).catch(() => {
        toast.error(pickText(language, "Failed to copy link.", "Impossibile copiare il link."));
      });
    }
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const params = {
          q: query || undefined,
          category: category === "all" ? undefined : category,
        };
        const data = await fetchLibrary(params);
        setEntries(data);
        // Only auto-select if no entry from URL
        if (!searchParams.get("entry")) {
          setSelectedEntry((current) => data.find((entry) => entry.slug === current?.slug) || data[0] || null);
        }
        setError("");
      } catch (error) {
        console.error(error);
        setEntries([]);
        setSelectedEntry(null);
        setError(pickText(language, "The archive could not be searched right now.", "L'archivio non può essere cercato in questo momento."));
        toast.error(pickText(language, "Archive search failed.", "La ricerca nell'archivio non è riuscita."));
      } finally {
        setLoading(false);
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [query, category]);

  return (
    <div className="space-y-10" data-testid="archive-page">
      <SectionIntro
        description={pickText(
          language,
          "Browse the full library of correspondences, from sefirot and chakras to symbols and structural zones.",
          "Sfoglia la libreria completa delle corrispondenze, dalle sefirot e i chakra fino ai simboli e alle zone strutturali.",
        )}
        eyebrow={pickText(language, "Archive", "Archivio")}
        testId="archive-intro"
        title={pickText(language, "Search the symbolic operating system.", "Cerca nel sistema operativo simbolico.")}
      />

      <ArchiveFilters
        category={category}
        language={language}
        onCategoryChange={setCategory}
        onQueryChange={setQuery}
        query={query}
      />

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]" data-testid="archive-layout-grid">
        <div className="space-y-4" data-testid="archive-results-list">
          {error ? (
            <div className="glass-card rounded-[2rem] border-red-500/20 p-5 text-sm text-red-200" data-testid="archive-error-state">
              {error}
            </div>
          ) : null}
          {loading ? (
            <div className="glass-card rounded-[2rem] p-5 text-sm text-slate-300" data-testid="archive-loading-state">
              {pickText(language, "Searching the archive…", "Ricerca nell'archivio…")}
            </div>
          ) : null}

          {!loading && entries.length === 0 ? (
            <div className="glass-card rounded-[2rem] p-5 text-sm text-slate-300" data-testid="archive-empty-state">
              {pickText(language, "No entries matched this search.", "Nessuna voce corrisponde a questa ricerca.")}
            </div>
          ) : null}

          {entries.map((entry) => {
            const active = entry.slug === selectedEntry?.slug;
            return (
              <button
                key={entry.slug}
                className={`glass-card block w-full rounded-[2rem] p-5 text-left transition-all duration-300 ${
                  active ? "border-primary/40 bg-primary/10" : "hover:border-primary/30"
                }`}
                data-testid={`archive-entry-button-${entry.slug}`}
                onClick={() => handleSelectEntry(entry)}
                type="button"
              >
                <p className="section-label" data-testid={`archive-entry-category-${entry.slug}`}>
                  {entry.category}
                </p>
                <h3 className="mt-3 text-3xl text-foreground" data-testid={`archive-entry-title-${entry.slug}`}>
                  {language === "it" ? entry.title_it : entry.title_en}
                </h3>
                <p className="mt-2 text-xs uppercase tracking-[0.24em] text-primary/80" data-testid={`archive-entry-subtitle-${entry.slug}`}>
                  {language === "it" ? entry.subtitle_it : entry.subtitle_en}
                </p>
                <p className="mt-4 text-sm leading-7 text-slate-300" data-testid={`archive-entry-description-${entry.slug}`}>
                  {language === "it" ? entry.description_it : entry.description_en}
                </p>
              </button>
            );
          })}
        </div>

        <div className="xl:sticky xl:top-32 xl:self-start" data-testid="archive-detail-column">
          {/* Copy Link Button */}
          {selectedEntry && (
            <div className="mb-4 flex justify-end">
              <Button
                className="ghost-button"
                data-testid="archive-copy-link-button"
                onClick={handleCopyLink}
                type="button"
              >
                <LinkIcon className="h-4 w-4" />
                {pickText(language, "Copy Link", "Copia Link")}
              </Button>
            </div>
          )}
          <EntryDetailPanel entry={selectedEntry} language={language} />
        </div>
      </div>
    </div>
  );
};